import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon,
  VpnKey as VpnKeyIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { fetchJson } from '../../../utils/fetchJson';
import useAyPhaseReady from '../../../utils/useAyPhaseReady';
import useCurrentUser from '../../../utils/useCurrentUser';

// Import the permission system
import { ROLES, PAGE_PERMISSIONS } from '../../../config/pagePermissions';

const RolePermissionPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { ready, activePhase, schoolId } = useAyPhaseReady();
  const { user } = useCurrentUser();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // roles: phase-aware, deduplicated working set used for tables and counts
  const [roles, setRoles] = useState([]);
  // systemTemplates: all global system templates (schoolId null, isSystemRole true), independent of phase/dedup
  const [systemTemplates, setSystemTemplates] = useState([]);
  const [error, setError] = useState(null);
  
  // Dialog states
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });

  // Permission management states
  const [expandedPermissions, setExpandedPermissions] = useState({});
  const [selectedPages, setSelectedPages] = useState({});
  // Route-based selection map to reflect server permissions exactly
  const [selectedRoutes, setSelectedRoutes] = useState({});
  // Delete confirmation dialog state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [rolePendingDelete, setRolePendingDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const MODULE_KEY_BY_NAME = React.useMemo(
    () => ({
      'Dashboard & Overview': 'dashboardOverview',
      'Class Management': 'classManagement',
      'Subject Management': 'subjectManagement',
      'Staff Management': 'staffManagement',
      'Student Management': 'studentManagement',
      Academics: 'academics',
      'Teaching & Learning': 'teachingLearning',
      Finance: 'finance',
      Facilities: 'facilities',
      'Health Management': 'healthManagement',
      Reports: 'reports',
      Communication: 'communication',
      'Support & Maintenance': 'supportMaintenance',
      'School Setup & Configuration': 'schoolSetupConfiguration',
      'Parent Portal': 'parentPortal',
    }),
    []
  );

  const PAGE_KEY_BY_NAME = React.useMemo(
    () => ({
      'School Overview': 'schoolOverview',
      'Quick Actions': 'quickActions',
      'Class Overview': 'classOverview',
      'Add New Class': 'addNewClass',
      'Manage Classes': 'manageClasses',
      'Class Assignments': 'classAssignments',
      'Class Reports': 'classReports',
      'Subject Overview': 'subjectOverview',
      'Add New Subject': 'addNewSubject',
      'Manage Subjects': 'manageSubjects',
      Departments: 'subjectAssignments',
      'Subject Assignments': 'subjectAssignments',
      'Subject Reports': 'subjectReports',
      'Department Reports': 'subjectReports',
      'Role Permissions': 'rolePermissionsPage',
      'User Management': 'userManagement',
      'User Form': 'userForm',
      'Staff Evaluation': 'staffEvaluationPage',
      'Performance Reviews': 'performanceReviews',
      'Training & Development': 'trainingDevelopment',
      'Leave Management': 'leaveManagement',
      Admissions: 'admissions',
      'Student Directory': 'studentDirectory',
      'Attendance Summary': 'attendanceSummary',
      'Students Discipline': 'studentsDiscipline',
      'Class Promotion Summary': 'classPromotionSummary',
      'ID Card Workshop': 'idCardWorkshop',
      'Curriculum Planning': 'curriculumPlanning',
      'Evaluation Control Center': 'evaluationControlCenter',
      'Academic Calendar': 'academicCalendar',
      Timetable: 'timetable',
      'Grading Scales': 'gradingScales',
      'Subject Hub': 'subjectHub',
      'Report Card Studio': 'reportCardStudio',
      'Student Grading': 'studentGrading',
      'Attendance Recording': 'attendanceRecording',
      'Class Discipline': 'classDiscipline',
      'Class Performance': 'classAcademicPerformance',
      'Subject Performance Matrix': 'subjectPerformanceMatrix',
      'Fees': 'feeStructure',
      'Fee Types': 'feeTypes',
      'Fee Payments': 'feePayments',
      Expenses: 'expenses',
      Budgets: 'budgets',
      'Financial Reports': 'financialReports',
      'Salary Management': 'salaryManagement',
      'Library Dashboard': 'libraryDashboard',
      'Catalog Management': 'catalogManagement',
      'Book Circulation': 'bookCirculation',
      'Book Issues': 'bookIssues',
      'Transport Routes': 'transportRoutes',
      'Transport Dashboard': 'transportDashboard',
      'Inventory Control': 'inventoryControl',
      'Health Records': 'healthRecords',
      'Health Incidents': 'healthIncidents',
      'Academic Reports': 'academicReports',
      Transcripts: 'transcripts',
      Announcements: 'announcements',
      'Emergency Alerts': 'emergencyAlerts',
      'Notification Center': 'notificationCenter',
      Messages: 'messages',
      'Submit Ticket': 'submitTicket',
      'Help Center': 'helpCenter',
      'School Settings': 'schoolSettings',
      'Academic Sessions': 'academicSessions',
      'Communication Settings': 'communicationSettings',
      'Backup & Security': 'backupSecurity',
      Dashboard: 'parentDashboard',
      'Children & Academics': 'childrenAcademics',
      Attendance: 'parentAttendance',
      'Fees & Payments': 'feesPayments',
    }),
    []
  );

  // Group pages by module with descriptions - using page names instead of paths
  // SANITISED TO THE SIDE MENUS — see packages/core/src/portal/pageCatalogue.js,
  // which the mobile role dialog uses. The two MUST agree: a page grantable on
  // one client and not the other means a role means different things depending
  // on where it was last saved.
  //
  // This is exactly the 54 pages reachable from the three portals a school
  // uses — School Admin, Teacher and Parent — grouped and named as those menus
  // do. Everything else was removed: the Health module no longer exists, and
  // /reports/*, /payroll, /scholarships, /curriculum-planning,
  // /academics/subject-hub, the library sub-pages, /performance-reviews,
  // /training-development, /leave-management and the rest have no menu entry in
  // any portal, so granting them unlocked a page nobody could navigate to.
  //
  // `route` is the SERVER path from PAGE_PERMISSIONS, carried alongside the
  // display name so NAME_TO_ROUTE and routeAlias cannot disagree with this list.
  // SANITISED TO THE SIDE MENUS — see packages/core/src/portal/pageCatalogue.js,
  // which the mobile role dialog uses. The two MUST agree: a page grantable on
  // one client and not the other means a role means different things depending
  // on where it was last saved.
  //
  // This is exactly the 53 pages reachable from the three portals a school
  // uses — School Admin, Teacher and Parent — grouped and named as those menus
  // do. Everything else was removed: the Health module no longer exists, and
  // /reports/*, /payroll, /scholarships, /curriculum-planning,
  // /academics/subject-hub, the library sub-pages, /performance-reviews,
  // /training-development, /leave-management and the rest have no menu entry in
  // any portal, so granting them unlocked a page nobody could navigate to.
  //
  // `route` is the SERVER path from PAGE_PERMISSIONS, carried alongside the
  // display name so NAME_TO_ROUTE and routeAlias cannot disagree with this list.
  // SANITISED TO THE SIDE MENUS — see packages/core/src/portal/pageCatalogue.js,
  // which the mobile role dialog uses. The two MUST agree: a page grantable on
  // one client and not the other means a role means different things depending
  // on where it was last saved.
  //
  // This is exactly the 56 pages reachable from the three portals a school
  // uses — School Admin, Teacher and Parent — grouped and named as those menus
  // do. Everything else was removed: the Health module no longer exists, and
  // /reports/*, /payroll, /scholarships, /curriculum-planning,
  // /academics/subject-hub, the library sub-pages, /performance-reviews,
  // /training-development, /leave-management and the rest have no menu entry in
  // any portal, so granting them unlocked a page nobody could navigate to.
  //
  // `route` is the SERVER path from PAGE_PERMISSIONS, carried alongside the
  // display name so NAME_TO_ROUTE and routeAlias cannot disagree with this list.
  // SANITISED TO THE SIDE MENUS — see packages/core/src/portal/pageCatalogue.js,
  // which the mobile role dialog uses. The two MUST agree: a page grantable on
  // one client and not the other means a role means different things depending
  // on where it was last saved.
  //
  // This is exactly the 54 pages reachable from the three portals a school
  // uses — School Admin, Teacher and Parent — grouped and named as those menus
  // do. Everything else was removed: the Health module no longer exists, and
  // /reports/*, /payroll, /scholarships, /curriculum-planning,
  // /academics/subject-hub, the library sub-pages, /performance-reviews,
  // /training-development, /leave-management and the rest have no menu entry in
  // any portal, so granting them unlocked a page nobody could navigate to.
  //
  // `route` is the SERVER path from PAGE_PERMISSIONS, carried alongside the
  // display name so NAME_TO_ROUTE and routeAlias cannot disagree with this list.
  const ALL_PAGE_MODULES = {
    'Dashboard & Overview': [
      { path: 'School Overview', route: '/school-overview' }
    ],
    'Class Management': [
      { path: 'Class Overview', route: '/class-management' },
      { path: 'Add New Class', route: '/class-management/add-class' },
      { path: 'Manage Classes', route: '/class-management/manage-classes' },
      { path: 'Class Assignments', route: '/class-management/class-assignments' },
      { path: 'Class Reports', route: '/class-management/class-reports' }
    ],
    'Subject Management': [
      { path: 'Subject Overview', route: '/subject-management' },
      { path: 'Add New Subject', route: '/subject-management/add-subject' },
      { path: 'Manage Subjects', route: '/subject-management/manage-subjects' },
      { path: 'Subject Assignments', route: '/subject-management/subject-assignments' },
      { path: 'Subject Reports', route: '/subject-management/subject-reports' }
    ],
    'Staff Management': [
      { path: 'Role Permissions', route: '/role-permissions' },
      { path: 'User Management', route: '/user-list' },
      { path: 'User Form', route: '/user-form' },
      { path: 'Staff Evaluation', route: '/staff-evaluation' }
    ],
    'Student Management': [
      { path: 'Admissions', route: '/student-admissions' },
      { path: 'Student Directory', route: '/student-directory' },
      { path: 'Attendance Summary', route: '/attendance-summary' },
      { path: 'Students Discipline', route: '/students-discipline' },
      { path: 'Class Promotion Summary', route: '/class-promotion-summary' },
      { path: 'ID Card Workshop', route: '/id-card-workshop' }
    ],
    'Academics': [
      { path: 'Evaluation Control Center', route: '/evaluation-control-center' },
      { path: 'Academic Calendar', route: '/calendar' },
      { path: 'Timetable', route: '/timetable' },
      { path: 'Grading Scales', route: '/grading-scales' },
      { path: 'Report Card Studio', route: '/academics/report-card-studio' },
      { path: 'Academic Transcripts', route: '/student-transcripts', secondaryOnly: true }
    ],
    'Teaching & Learning': [
      { path: 'Take attendance', route: '/teacher/attendance-recording' },
      { path: 'Enter marks', route: '/teacher/student-grading' },
      { path: 'Class results', route: '/classmaster/class-academic-performance' },
      { path: 'Class discipline', route: '/classmaster/class-discipline' },
      { path: 'Subject performance', route: '/subjecthead/subject-performance-matrix', secondaryOnly: true },
      { path: 'Departmental report', route: '/subjecthead/subject-report', secondaryOnly: true }
    ],
    'Finance': [
      { path: 'Fee Types', route: '/fee-types' },
      { path: 'Fees', route: '/finance/fee-structures' },
      { path: 'Payment Processing', route: '/finance/payments' },
      { path: 'Manage Fee Payments', route: '/finance/manage-fee-payments' },
      { path: 'Expenses', route: '/expenses' },
      { path: 'Budgets', route: '/budgets' },
      { path: 'Financial Reports', route: '/financial-reports' },
      { path: 'Salary Management', route: '/salary-management' }
    ],
    'Facilities': [
      { path: 'Library Dashboard', route: '/facilities/library-dashboard' },
      { path: 'Transport Routes', route: '/facilities/transport-routes' },
      { path: 'Inventory Control', route: '/facilities/inventory-control' }
    ],
    'Communication': [
      { path: 'Announcements', route: '/communication/announcements' }
    ],
    'Support & Maintenance': [
      { path: 'Submit Ticket', route: '/support/submit-ticket' },
      { path: 'Help Center', route: '/support/help-center' }
    ],
    'School Setup & Configuration': [
      { path: 'School Settings', route: '/school-setup-and-configuration/school-settings' },
      { path: 'Communication Settings', route: '/school-setup-and-configuration/communication-settings' },
      { path: 'Backup & Security', route: '/school-setup-and-configuration/backup-security' }
    ],
    'Parent Portal': [
      { path: 'Dashboard', route: '/parent/dashboard' },
      { path: 'Children & Academics', route: '/parent/children-academics' },
      { path: 'Attendance Records', route: '/parent/attendance' },
      { path: 'Fees & Payments', route: '/parent/fees-payments' }
    ]
  };

  // SECONDARY-ONLY PAGES ARE NOT OFFERED IN NURSERY/PRIMARY.
  //
  // Ticking one there produced a permission that could never be exercised: the
  // route is wrapped in SecPhaseRoute and every endpoint behind it refuses the
  // phase. An offer that cannot be honoured is worse than no offer — somebody
  // grants it, saves, and the page still does not appear, with nothing on
  // screen to say why.
  //
  // Only filtered once the phase is KNOWN; an unresolved phase shows the full
  // catalogue rather than hiding pages an admin came here looking for. Mirrors
  // visiblePageCatalogue in @ameca/core, which the phone's role dialog uses —
  // a page grantable on one client and not the other would mean a role meant
  // different things depending on where it was last saved.
  const pageModules = React.useMemo(() => {
    const nurPri = String(activePhase || '').trim().toLowerCase() === 'nur_pri';
    if (!nurPri) return ALL_PAGE_MODULES;
    const out = {};
    Object.entries(ALL_PAGE_MODULES).forEach(([mod, pages]) => {
      const kept = pages.filter((p) => !p.secondaryOnly);
      if (kept.length) out[mod] = kept;
    });
    return out;
  }, [activePhase]);

  // Precompute a Set of valid page permission names for filtering
  const allPageNames = React.useMemo(() => {
    const names = [];
    Object.keys(pageModules).forEach(m => pageModules[m].forEach(p => names.push(p.path)));
    return new Set(names);
  }, []);

  // Map a route path like '/teacher/student-grading' to a page name like 'Student Grading'
  const routeAlias = React.useMemo(() => ({
    '/school-overview': 'School Overview',
    '/quick-actions': 'Quick Actions',
    '/user-list': 'User Management',
    '/user-form': 'User Form',
    '/role-permissions': 'Role Permissions',
    '/staff-evaluation': 'Staff Evaluation',
    
    '/finance/fee-structures': 'Fees',
    '/fee-types': 'Fee Types',
    '/finance/payments': 'Fee Payments',
    '/expenses': 'Expenses',
    '/budgets': 'Budgets',
    '/financial-reports': 'Financial Reports',
    '/salary-management': 'Salary Management',
    
    '/facilities/library-dashboard': 'Library Dashboard',
    '/facilities/library/catalog': 'Catalog Management',
    '/facilities/library/circulation': 'Book Circulation',
    '/facilities/library/issues': 'Book Issues',
    '/facilities/transport-routes': 'Transport Routes',
    '/facilities/transport-dashboard': 'Transport Dashboard',
    '/facilities/inventory-control': 'Inventory Control',
    '/health/records': 'Health Records',
    '/health/incidents': 'Health Incidents',
    '/reports/academic': 'Academic Reports',
    '/reports/transcripts': 'Transcripts',
    '/communication/announcements': 'Announcements',
    '/communication/emergency-alerts': 'Emergency Alerts',
    '/communication/notification-center': 'Notification Center',
    '/communication/messages': 'Messages',
    '/my-profile': 'My Profile',
    '/support/submit-ticket': 'Submit Ticket',
    '/support/help-center': 'Help Center',
    '/school-setup-and-configuration/school-settings': 'School Settings',
    '/school-setup-and-configuration/academic-sessions': 'Academic Sessions',
    '/school-setup-and-configuration/communication-settings': 'Communication Settings',
    '/school-setup-and-configuration/backup-security': 'Backup & Security',
    '/student-admissions': 'Admissions',
    '/student-directory': 'Student Directory',
    '/attendance-summary': 'Attendance Summary',
    '/students-discipline': 'Students Discipline',
    '/class-promotion-summary': 'Class Promotion Summary',
    '/id-card-workshop': 'ID Card Workshop',
    '/evaluation-control-center': 'Evaluation Control Center',
    '/calendar': 'Academic Calendar',
    '/timetable': 'Timetable',
    '/grading-scales': 'Grading Scales',
    '/academics/subject-hub': 'Subject Hub',
    '/academics/report-card-studio': 'Report Card Studio',
    '/class-management': 'Class Overview',
    '/class-management/add-class': 'Add New Class',
    '/class-management/manage-classes': 'Manage Classes',
    '/class-management/class-assignments': 'Class Assignments',
    '/class-management/class-reports': 'Class Reports',
    '/subject-management': 'Subject Overview',
    '/subject-management/add-subject': 'Add New Subject',
    '/subject-management/manage-subjects': 'Manage Subjects',
    '/subject-management/subject-assignments': 'Departments',
    '/subject-management/subject-reports': 'Subject Reports',
    '/teacher/student-grading': 'Student Grading',
    '/teacher/attendance-recording': 'Attendance Recording',
    '/teacher/class-discipline': 'Class Discipline',
    '/classmaster/class-academic-performance': 'Class Performance',
    '/subjecthead/subject-performance-matrix': 'Subject Performance Matrix',
    '/parent/dashboard': 'Dashboard',
    '/parent/children-academics': 'Children & Academics',
    '/parent/attendance': 'Attendance',
    '/parent/fees-payments': 'Fees & Payments',
    '/parent/messages': 'Messages',
  }), []);

  // Explicit mapping from dialog page names to backend route paths
  const NAME_TO_ROUTE = React.useMemo(() => ({
    'School Overview': '/school-overview',
    'Quick Actions': '/quick-actions',
    'Class Overview': '/class-management',
    'Add New Class': '/class-management/add-class',
    'Manage Classes': '/class-management/manage-classes',
    'Class Assignments': '/class-management/class-assignments',
    'Class Reports': '/class-management/class-reports',
    'Subject Overview': '/subject-management',
    'Add New Subject': '/subject-management/add-subject',
    'Manage Subjects': '/subject-management/manage-subjects',
    Departments: '/subject-management/subject-assignments',
    'Subject Assignments': '/subject-management/subject-assignments',
    'Subject Reports': '/subject-management/subject-reports',
    'Department Reports': '/subject-management/subject-reports',
    'Role Permissions': '/role-permissions',
    'User Management': '/user-list',
    'User Form': '/user-form',
    'Staff Evaluation': '/staff-evaluation',
    
    'Admissions': '/student-admissions',
    'Student Directory': '/student-directory',
    'Attendance Summary': '/attendance-summary',
    'Students Discipline': '/students-discipline',
    'Class Promotion Summary': '/class-promotion-summary',
    'ID Card Workshop': '/id-card-workshop',
    'Evaluation Control Center': '/evaluation-control-center',
    'Academic Calendar': '/calendar',
    'Timetable': '/timetable',
    'Grading Scales': '/grading-scales',
    'Subject Hub': '/academics/subject-hub',
    'Report Card Studio': '/academics/report-card-studio',
    'Student Grading': '/teacher/student-grading',
    'Attendance Recording': '/teacher/attendance-recording',
    'Class Discipline': '/teacher/class-discipline',
    'Class Performance': '/classmaster/class-academic-performance',
    'Subject Performance Matrix': '/subjecthead/subject-performance-matrix',
    'Fees': '/finance/fee-structures',
    'Fee Types': '/fee-types',
    'Fee Payments': '/finance/payments',
    'Expenses': '/expenses',
    'Budgets': '/budgets',
    'Financial Reports': '/financial-reports',
    'Salary Management': '/salary-management',
    
    'Library Dashboard': '/facilities/library-dashboard',
    'Catalog Management': '/facilities/library/catalog',
    'Book Circulation': '/facilities/library/circulation',
    'Book Issues': '/facilities/library/issues',
    'Transport Routes': '/facilities/transport-routes',
    'Transport Dashboard': '/facilities/transport-dashboard',
    'Inventory Control': '/facilities/inventory-control',
    'Health Records': '/health/records',
    'Health Incidents': '/health/incidents',
    'Academic Reports': '/reports/academic',
    'Operational Reports': '/reports/operational',
    'Custom Reports': '/reports/custom',
    'Data Exporter': '/reports/data-exporter',
    'Announcements': '/communication/announcements',
    'Emergency Alerts': '/communication/emergency-alerts',
    'Notification Center': '/communication/notification-center',
    'Messages': '/communication/messages',
    'Submit Ticket': '/support/submit-ticket',
    'Help Center': '/support/help-center',
    'School Settings': '/school-setup-and-configuration/school-settings',
    'Academic Sessions': '/school-setup-and-configuration/academic-sessions',
    'Communication Settings': '/school-setup-and-configuration/communication-settings',
    'Backup & Security': '/school-setup-and-configuration/backup-security',
    // Student Portal
    // Parent Portal
    'Children & Academics': '/parent/children-academics',
    'Attendance': '/parent/attendance',
    'Fees & Payments': '/parent/fees-payments',
    'Parent Portal Messages': '/parent/messages'
  }), []);
  const pathToName = (path) => {
    if (!path) return null;
    if (routeAlias[path]) return routeAlias[path];
    const seg = String(path).split('/').filter(Boolean).pop() || '';
    if (!seg) return null;
    const name = seg.replace(/[-_]/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());
    return name;
  };

  const nameToRoute = React.useMemo(() => {
    // reverse of routeAlias plus heuristic slug mapping
    const map = {};
    // start with explicit names mapping
    Object.entries(NAME_TO_ROUTE).forEach(([name, route]) => { map[name] = route; });

    // pageModules is AUTHORITATIVE. Each entry now carries its own server
    // `route`, and it wins over NAME_TO_ROUTE and routeAlias below.
    //
    // Those two maps are hand-maintained and were already incomplete: ten of
    // the sanitised names — Take attendance, Enter marks, Class results, Class
    // discipline, Subject performance, Departmental report, Payment
    // Processing, Manage Fee Payments, Dashboard, Attendance Records — appear
    // in no map, so ticking them resolved to no route and the permission was
    // dropped on save without a word. Carrying the route beside the name makes
    // that class of mismatch impossible.
    Object.values(pageModules).forEach((pages) => {
      pages.forEach((p) => { if (p.route) map[p.path] = p.route; });
    });
    Object.entries(routeAlias).forEach(([route, name]) => {
      map[name] = route;
    });
    const slugify = (s) => '/' + s.toLowerCase().replace(/\s+/g, '-');
    // Provide fallback guesses for names not in alias by scanning common root prefixes
    const commonPrefixes = [
      '',
      '/teacher', '/classmaster', '/subjecthead', '/finance', '/facilities', '/health', '/reports',
      '/communication', '/support', '/school-setup-and-configuration', '/class-management', '/subject-management',
      '/student', '/parent'
    ];
    Object.keys(pageModules).forEach(module => {
      pageModules[module].forEach(p => {
        if (!map[p.path]) {
          // try exact slug with known section prefixes
          for (const pref of commonPrefixes) {
            const guess = pref ? `${pref}${slugify(p.path)}` : slugify(p.path);
            // Only record if the route exists in PAGE_PERMISSIONS keys client-side (best-effort)
            if (Object.prototype.hasOwnProperty.call(PAGE_PERMISSIONS, guess)) {
              map[p.path] = guess;
              break;
            }
          }
        }
      });
    });
    return map;
  }, [routeAlias]);

  const selectedRouteNames = React.useMemo(() => {
    const set = new Set();
    Object.keys(selectedRoutes || {}).forEach(r => {
      if (selectedRoutes[r]) {
        const nm = pathToName(r);
        if (nm) set.add(nm);
      }
    });
    return set;
  }, [selectedRoutes]);

  // Roles allowed for default templates based on PAGE_PERMISSIONS usage
  const allowedTemplateRoles = React.useMemo(() => {
    const set = new Set();
    Object.values(PAGE_PERMISSIONS || {}).forEach(arr => {
      (arr || []).forEach(r => set.add(r));
    });
    return set;
  }, []);

  // Filter out Super Admin roles and system-level roles
  const getSchoolLevelRoles = () => {
    return Object.values(ROLES).filter(role => 
      role !== ROLES.SUPER_ADMIN && 
      role !== ROLES.SCHOOL_ADMIN
    );
  };

  // Get default pages for a specific role based on role menu configuration
  const getDefaultPagesForRole = (roleName) => {
    const defaultPages = {};
    
    // Initialize all pages as unchecked
    Object.keys(pageModules).forEach(module => {
      pageModules[module].forEach(page => {
        defaultPages[page.path] = false;
      });
    });

    // Get role-specific pages from our role menu configuration
    const roleMenuPages = getRoleMenuPages(roleName);
    
    // Apply the role-specific permissions
    Object.keys(roleMenuPages).forEach(pagePath => {
      if (roleMenuPages[pagePath]) {
        defaultPages[pagePath] = true;
      }
    });

    return defaultPages;
  };

    // Get role-specific default pages using pageModules names
  // This implements the full page access defaults per role as discussed.
  const getRoleMenuPages = (roleName) => {
    const normalizedRoleName = (roleName || '').toLowerCase().replace(/\s+/g, '');
    const rolePages = {};
    // initialize all to false
    Object.keys(pageModules).forEach(module => {
      pageModules[module].forEach(page => { rolePages[page.path] = false; });
    });

    const enable = (names = []) => {
      names.forEach(n => { if (n in rolePages) rolePages[n] = true; });
    };
    const enableModule = (moduleName) => {
      (pageModules[moduleName] || []).forEach(p => { rolePages[p.path] = true; });
    };
    const enableAll = () => {
      Object.keys(pageModules).forEach(m => enableModule(m));
    };

    switch (normalizedRoleName) {
      case 'systemadmin':
        // System-level admin typically not managed here; leave empty or minimal
        enable(['School Overview','Quick Actions']);
        break;
      case 'schooladmin':
        // SchoolAdmin has all school permissions (all page modules)
        enableAll();
        break;
      case 'principal':
        // Broad school access; backend will phase-scope data to SEC
        enableAll();
        // Remove School Setup & Configuration module pages
        ['School Settings','Academic Sessions','Communication Settings','Backup & Security'].forEach(n => { if (n in rolePages) rolePages[n] = false; });
        // Remove Dashboard & Overview module pages
        ['School Overview','Quick Actions'].forEach(n => { if (n in rolePages) rolePages[n] = false; });
        // Remove Salary Management from Finance
        ['Salary Management'].forEach(n => { if (n in rolePages) rolePages[n] = false; });
        break;
      case 'headmaster':
        // Broad school access; backend will phase-scope data to NUR_PRI
        enableAll();
        // Remove School Setup & Configuration module pages
        ['School Settings','Academic Sessions','Communication Settings','Backup & Security'].forEach(n => { if (n in rolePages) rolePages[n] = false; });
        // Remove Dashboard & Overview module pages
        ['School Overview','Quick Actions'].forEach(n => { if (n in rolePages) rolePages[n] = false; });
        // Remove Salary Management from Finance
        ['Salary Management'].forEach(n => { if (n in rolePages) rolePages[n] = false; });
        // Remove ID Card Workshop from Student Management
        ['ID Card Workshop'].forEach(n => { if (n in rolePages) rolePages[n] = false; });
        // Ensure HeadMaster has all Subject Management and Reports module pages explicitly
        enableModule('Subject Management');
        enableModule('Reports');
        break;
      case 'teacher':
        enable(['Student Grading','Attendance Recording','Messages']);
        break;
      case 'classmaster':
        enable(['Class Discipline','Class Performance']);
        break;
      case 'subjecthead':
        enable(['Subject Performance Matrix']);
        break;
      case 'financeofficer':
        enableModule('Finance');
        // Optional comms used by finance per server map
        enable(['Announcements','Emergency Alerts','Notification Center']);
        break;
      case 'schoolitcoordinator':
        // IT Coordinator focuses on setup, support and communications
        enableModule('School Setup & Configuration');
        enableModule('Support & Maintenance');
        enable(['Announcements','Emergency Alerts','Notification Center']);
        break;
      case 'itsupport':
        // IT Support handles support/maintenance and limited setup
        enableModule('Support & Maintenance');
        enable(['Announcements','Emergency Alerts','Notification Center']);
        break;
      case 'librarian':
        enable(['Library Dashboard','Catalog Management','Book Circulation','Book Issues']);
        break;
      case 'transportofficer':
        enable(['Transport Routes','Transport Dashboard']);
        break;
      case 'inventorymanager':
        enable(['Inventory Control']);
        break;
      case 'admissionsofficer':
        enable(['Admissions','Student Directory','ID Card Workshop']);
        break;
      case 'student':
        enable(['Dashboard','My Academics','Attendance Records']);
        break;
      case 'parent':
        enable(['Dashboard','Children & Academics','Attendance','Fees & Payments','Messages']);
        break;
      default:
        break;
    }
    return rolePages;
  };

  const getRolePhaseCode = (role) => {
    if (!role) return null;
    let p = null;
    if (role.phaseCode) {
      p = String(role.phaseCode).toLowerCase();
    } else if (role.metadata && role.metadata.phaseCode) {
      p = String(role.metadata.phaseCode).toLowerCase();
    }
    if (!p && role.metadata && Array.isArray(role.metadata.phaseRestrictions)) {
      const pr = role.metadata.phaseRestrictions.map(x => String(x).toLowerCase());
      const hasSec = pr.includes('sec');
      const hasNur = pr.includes('nur_pri');
      if (hasSec && !hasNur) p = 'sec';
      else if (hasNur && !hasSec) p = 'nur_pri';
    }
    if (!p) {
      const nmSlug = (role.name || '').toString().toLowerCase().replace(/\s+/g, '');
      if (nmSlug === 'principal') p = 'sec';
      else if (nmSlug === 'headmaster') p = 'nur_pri';
    }
    return p;
  };

  useEffect(() => {
    if (!ready) return;
    fetchRoles();
  }, [ready, activePhase]);

  // Auto-refresh on phase/AY switches
  useEffect(() => {
    const onPhase = () => { if (ready) fetchRoles(); };
    const onAy = () => { if (ready) fetchRoles(); };
    window.addEventListener('app:phase:changed', onPhase);
    window.addEventListener('app:ay:changed', onAy);
    return () => {
      window.removeEventListener('app:phase:changed', onPhase);
      window.removeEventListener('app:ay:changed', onAy);
    };
  }, [ready]);

  const fetchRoles = async () => {
    let maxGuard;
    try {
      setLoading(true);
      setError(null);

      // Build context cache key (phase + AY + school)
      const schoolIdKey = String(schoolId || localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '');
      const phaseIdKey = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCodeKey = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const ayKeyName = schoolIdKey ? `${schoolIdKey}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayIdKey = localStorage.getItem(ayKeyName) || '';
      const ctxKey = `${schoolIdKey}|${phaseIdKey || phaseCodeKey || '-'}|${ayIdKey || '-'}`;
      const cacheKey = `rolePermissions:roles:v4:${ctxKey}`;

      // Prefill from cache to avoid flash
      try {
        const raw = localStorage.getItem(cacheKey);
        if (raw) {
          const cached = JSON.parse(raw);
          if (Array.isArray(cached)) setRoles(cached);
        }
      } catch(_) {}

      // 2s guard to avoid feeling stuck
      try { maxGuard = setTimeout(() => setLoading(false), 2000); } catch(_) {}
      
      const headers = {
        ...(schoolIdKey ? { 'X-Scope-School-Id': schoolIdKey } : {}),
        ...(phaseCodeKey ? { 'X-Active-Phase': phaseCodeKey } : {})
      };
      const rolesData = await fetchJson('/api/roles', { headers });
      const rolesArray = Array.isArray(rolesData) ? rolesData : 
                         (rolesData?.roles || rolesData?.data || []);

      // Cache the result for next time
      try {
        localStorage.setItem(cacheKey, JSON.stringify(rolesArray));
      } catch(_) {}

      // Hard-sanitize roles to the current school context (system templates OR school custom roles)
      const scopedRolesArray = (Array.isArray(rolesArray) ? rolesArray : []).filter((r) => {
        if (!r) return false;
        if (!schoolIdKey) return true;
        return r.schoolId == null || String(r.schoolId) === String(schoolIdKey);
      });

      // Normalize active phase once for this fetch
      const phase = (activePhase || '').toLowerCase();

      const viewerRoleCanonical = (user?.roleNormalized || user?.role || user?.Role?.name || '').toString();
      const viewerRoleSlug = viewerRoleCanonical.toLowerCase().replace(/\s+/g, '').replace(/[^a-z]/g, '');
      const viewerIsSchoolAdmin = viewerRoleSlug === 'schooladmin';

      const applyLeadershipPhaseFilter = (arr) => {
        const list = Array.isArray(arr) ? arr : [];
        if (!phase) return list;
        return list.filter((r) => {
          const nmSlug = (r?.name || '').toString().toLowerCase().replace(/\s+/g, '');
          if (phase === 'sec' && nmSlug === 'headmaster') return false;
          if (phase === 'nur_pri' && nmSlug === 'principal') return false;
          return true;
        });
      };

      // Capture global school templates (schoolId=null, isTemplate=true) for display in the templates section.
      // Backend now only returns school-scoped templates (not system-scoped like SystemAdmin/ITManager).
      try {
        let sysTemplates = scopedRolesArray.filter(role => {
          const nm = (role.name || '').toLowerCase();
          const isGlobalSchoolTemplate = role.schoolId == null && role.isTemplate === true;
          if (!isGlobalSchoolTemplate) return false;
          if (nm === 'systemadmin') return false;
          if (!viewerIsSchoolAdmin && nm === 'schooladmin') return false;
          if (nm === 'principalheadteacher') return false;
          // Student is excluded because it is not a real role: there is no
          // Student row in the roles table for any school, and the block in
          // roleMenus is vestigial.
          if (nm === 'student') return false;
          if (nm === 'nursehealthofficer') return false;
          return true;
        });

        // Phase-aware filtering for global system templates when an active phase is set
        if (phase) {
          sysTemplates = sysTemplates.filter(r => {
            const effPhase = getRolePhaseCode(r);
            if (effPhase && effPhase !== phase) return false;
            return true;
          });
        }

        // Explicitly enforce principal/headmaster phase restrictions even when metadata is missing
        sysTemplates = applyLeadershipPhaseFilter(sysTemplates);

        setSystemTemplates(sysTemplates);
      } catch(_) {}

      // Hide SystemAdmin, legacy PrincipalHeadTeacher and Student from the
      // working set; SchoolAdmin and Parent stay visible. Parent is a real
      // portal role with its own menu block and its own page set, so it has to
      // be manageable here like any other.
      let visible = scopedRolesArray.filter(role => {
        const nm = (role.name || '').toLowerCase();
        if (nm === (ROLES.SYSTEM_ADMIN || 'systemadmin').toLowerCase()) return false;
        if (!viewerIsSchoolAdmin && nm === (ROLES.SCHOOL_ADMIN || 'schooladmin').toLowerCase()) return false;
        if (nm === 'principalheadteacher') return false;
        if (nm === 'student') return false;
        if (nm === 'nursehealthofficer') return false;
        return true;
      });

      // Phase-aware filtering for all viewers (SchoolAdmin + heads)
      if (phase) {
        visible = visible.filter(r => {
          const effPhase = getRolePhaseCode(r);
          if (effPhase && effPhase !== phase) return false;
          return true;
        });
      }

      // Explicitly enforce principal/headmaster phase restrictions even when metadata is missing
      visible = applyLeadershipPhaseFilter(visible);

      // Resolve duplicates: if the school has a customized role based on a global template,
      // that school role should override (replace) the system template in the working set.
      const normKey = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
      const sid = String(schoolId || '');
      const schoolScoped = visible.filter(r => r.schoolId != null && String(r.schoolId) === sid);
      const globalTemplates = visible.filter(r => r.schoolId == null && r.isTemplate === true);

      const overriddenTemplateIds = new Set(
        schoolScoped
          .map(r => r?.templateRoleId)
          .filter(x => x != null)
          .map(x => String(x))
      );
      const overriddenNames = new Set(schoolScoped.map(r => normKey(r?.name)));

      const resolved = [
        ...schoolScoped,
        ...globalTemplates.filter(tpl => {
          const id = tpl?.id != null ? String(tpl.id) : null;
          if (id && overriddenTemplateIds.has(id)) return false;
          if (overriddenNames.has(normKey(tpl?.name))) return false;
          return true;
        })
      ];

      setRoles(resolved);
      try { localStorage.setItem(cacheKey, JSON.stringify(resolved)); } catch(_) {}
    } catch (error) {
      console.error('Error fetching roles:', error);
      setError(t('rolePermissions.errors.fetchRoles', { message: error.message }));
    } finally {
      setLoading(false);
      try { clearTimeout(maxGuard); } catch(_) {}
    }
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setRoleFormData({
      name: '',
      description: '',
      permissions: [],
      templateRoleId: null
    });
    setSelectedPages({});
    setSelectedRoutes({});
    setRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    if (!roleFormData.name.trim()) {
      enqueueSnackbar(t('rolePermissions.errors.roleNameRequired'), { variant: 'error' });
      return;
    }

    if (roleFormData.name.trim().toLowerCase().replace(/\s+/g,'') === 'systemadmin') {
      enqueueSnackbar(t('rolePermissions.errors.cannotModifySystemAdmin'), { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);

      // Convert selections to route array (source-of-truth)
      const routeSet = new Set();
      // Include any explicitly selected routes
      Object.keys(selectedRoutes).forEach(r => { if (selectedRoutes[r]) routeSet.add(r); });
      // Include selected page names mapped to routes
      Object.keys(selectedPages).forEach(name => {
        if (selectedPages[name]) {
          const route = nameToRoute[name];
          if (route) routeSet.add(route);
        }
      });
      const permissions = Array.from(routeSet);

      const roleData = {
        name: roleFormData.name.trim(),
        description: roleFormData.description.trim(),
        permissions,
        isTemplate: false,
        ...(roleFormData.templateRoleId ? { templateRoleId: roleFormData.templateRoleId } : {})
      };

      if (editingRole) {
        // Update existing role
        // Do not allow changing the originating template on update.
        // Backend enforces template rules; we omit templateRoleId here intentionally.
        delete roleData.templateRoleId;
        await fetchJson(`/api/roles/${editingRole.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(roleData)
        });
        enqueueSnackbar(t('rolePermissions.success.roleUpdated'), { variant: 'success' });
      } else {
        // Create new role
        await fetchJson('/api/roles', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(roleData)
        });
        enqueueSnackbar(t('rolePermissions.success.roleCreated'), { variant: 'success' });
      }

      setRoleDialogOpen(false);
      fetchRoles();
      
    } catch (error) {
      console.error('Error saving role:', error);
      enqueueSnackbar(t('rolePermissions.errors.savingRole', { message: error.message }), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle role name change to auto-populate default permissions
  const handleRoleNameChange = (roleName) => {
    setRoleFormData(prev => ({ ...prev, name: roleName }));
    
    // Auto-populate default pages based on role name
    if (roleName.trim()) {
      // Get default pages from role menu configuration
      const defaultPages = getDefaultPagesForRole(roleName.trim());
      setSelectedPages(defaultPages);
      // Also seed routes based on defaults
      const routeMap = {};
      Object.keys(defaultPages).forEach(name => {
        if (defaultPages[name]) {
          const route = nameToRoute[name] || NAME_TO_ROUTE[name];
          if (route) routeMap[route] = true;
        }
      });
      setSelectedRoutes(routeMap);
    } else {
      setSelectedPages({});
      setSelectedRoutes({});
    }
  };

  const handleEditRole = (role) => {
    // Prevent editing only for global system roles; allow viewing/editing school-scoped templates
    const isGlobalSystem = role.isTemplate === true && (role.schoolId == null);
    if (isGlobalSystem) {
      enqueueSnackbar(t('rolePermissions.errors.systemRolesCannotBeEdited'), { 
        variant: 'warning' 
      });
      return;
    }

    setEditingRole(role);
    setRoleFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || [],
      templateRoleId: role.templateRoleId || null
    });
    
    // Get default permissions for this role
    const defaultPages = getRoleMenuPages(role.name);
    
    // Initialize all pages as unchecked first
    const currentPages = {};
    Object.keys(pageModules).forEach(module => {
      pageModules[module].forEach(page => {
        currentPages[page.path] = false;
      });
    });
    
    // Apply server permissions by mapping page names to expected routes using reverse map
    const routeList = new Set([
      ...(Array.isArray(role.permissions) ? role.permissions : []),
      ...(Array.isArray(role.Permissions) ? role.Permissions.map(p => p?.name).filter(Boolean) : [])
    ]);
    if (routeList.size > 0) {
      // Ensure every route checks all associated dialog page names via explicit inverse map
      const routeToNames = {};
      Object.entries(NAME_TO_ROUTE).forEach(([name, route]) => {
        if (!routeToNames[route]) routeToNames[route] = [];
        routeToNames[route].push(name);
      });
      routeList.forEach(route => {
        const names = routeToNames[route];
        if (Array.isArray(names)) {
          names.forEach(nm => { if (nm in currentPages) currentPages[nm] = true; });
        }
      });

      Object.keys(currentPages).forEach(pageName => {
        const route = nameToRoute[pageName];
        if (route && routeList.has(route)) {
          currentPages[pageName] = true;
        }
      });
      // Fallback: direct route -> name mapping using pathToName for any remaining
      routeList.forEach(route => {
        const name = pathToName(route);
        if (name && Object.prototype.hasOwnProperty.call(currentPages, name)) {
          currentPages[name] = true;
        }
      });
    } else {
      // No routes from server (defensive): seed from client defaults
      Object.keys(defaultPages).forEach(pageName => {
        if (defaultPages[pageName]) {
          currentPages[pageName] = true;
        }
      });
    }

    // Also persist exact route selections so checkboxes can reflect server routes directly
    const initialRoutes = {};
    if (routeList.size > 0) {
      routeList.forEach(r => { initialRoutes[r] = true; });
    } else {
      // Fill initialRoutes from defaults using explicit name->route mapping
      Object.keys(defaultPages).forEach(pageName => {
        if (defaultPages[pageName]) {
          const route = nameToRoute[pageName] || NAME_TO_ROUTE[pageName];
          if (route) initialRoutes[route] = true;
        }
      });
    }
    
    // Apply default permissions for pages not in database
    Object.keys(defaultPages).forEach(pagePath => {
      if (defaultPages[pagePath] && !currentPages[pagePath]) {
        currentPages[pagePath] = true;
      }
    });

    // Enforce role-specific exclusions after merging DB + defaults
    const roleNameNorm = (role?.name || '').toLowerCase().replace(/\s+/g, '');
    if (roleNameNorm === 'headmaster') {
      if (Object.prototype.hasOwnProperty.call(currentPages, 'ID Card Workshop')) {
        currentPages['ID Card Workshop'] = false;
      }
    }

    // Dev-only: log what will be checked for quick verification
    if (process.env.NODE_ENV !== 'production') {
      try {
        const checked = Object.entries(currentPages).filter(([, v]) => v).map(([k]) => k);
        console.debug('[RolePermissionPage] Pre-check summary:', {
          role: role?.name,
          checkedCount: checked.length,
          sampleChecked: checked.slice(0, 10)
        });
      } catch (e) { /* no-op */ }
    }

    setSelectedPages(currentPages);
    setSelectedRoutes(initialRoutes);
    setRoleDialogOpen(true);
    return; // End of edit path
  };

  const performDeleteRole = async (role) => {
    // Prevent deletion of global system roles only; allow deleting school-scoped templates even if mis-flagged
    const isGlobalSystem = role.isTemplate === true && (role.schoolId == null);
    if (isGlobalSystem) {
      enqueueSnackbar(t('rolePermissions.errors.systemRolesCannotBeDeleted'), { 
        variant: 'warning' 
      });
      return;
    }

    try {
      setDeleteError('');
      setDeleteLoading(true);
      await fetchJson(`/api/roles/${role.id}`, {
        method: 'DELETE'
      });
      enqueueSnackbar(t('rolePermissions.success.templateDeleted'), { variant: 'success' });
      setConfirmDeleteOpen(false);
      setRolePendingDelete(null);
      // Optimistically update table to remove the deleted item
      setRoles(prev => prev.filter(r => String(r.id) !== String(role.id)));
      fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      enqueueSnackbar(t('rolePermissions.errors.deletingTemplate', { message: error.message }), { variant: 'error' });
      setDeleteError(error?.message || t('rolePermissions.errors.deleteTemplateFailed'));
    }
    finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteClick = (role) => {
    setRolePendingDelete(role);
    setConfirmDeleteOpen(true);
  };
  const handleConfirmDelete = () => {
    if (rolePendingDelete) performDeleteRole(rolePendingDelete);
  };
  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setRolePendingDelete(null);
  };

  // Handle creating role from template
  const handleCreateFromTemplate = (templateRole) => {
    setEditingRole(null);
    // Determine initial permissions: use template permissions if available, otherwise role defaults
    const defaultsMap = getRoleMenuPages(templateRole.name);
    const defaultPermissionNames = Object.keys(defaultsMap).filter(p => defaultsMap[p]);
    // If templateRole.permissions are provided as routes (server shape), prefer them
    const initialRoutes = new Set();
    if (Array.isArray(templateRole.permissions) && templateRole.permissions.length) {
      templateRole.permissions.forEach(r => initialRoutes.add(r));
    } else {
      // Map default page names to routes via NAME_TO_ROUTE
      defaultPermissionNames.forEach(name => {
        const r = nameToRoute[name] || NAME_TO_ROUTE[name];
        if (r) initialRoutes.add(r);
      });
    }

    setRoleFormData({
      name: `${templateRole.name} ${t('rolePermissions.customRoleSuffix')}`,
      description: t('rolePermissions.customRoleDescription', { name: templateRole.name }),
      permissions: Array.from(initialRoutes),
      templateRoleId: templateRole.id
    });
    
    // Set selected pages to initial permissions
    const templatePages = {};
    Object.keys(pageModules).forEach(module => {
      pageModules[module].forEach(page => {
        const route = nameToRoute[page.path] || NAME_TO_ROUTE[page.path];
        templatePages[page.path] = route ? initialRoutes.has(route) : false;
      });
    });
    
    setSelectedPages(templatePages);
    // Also seed selected routes directly so checkboxes reflect server routes immediately
    const routesMap = {};
    initialRoutes.forEach(r => { routesMap[r] = true; });
    setSelectedRoutes(routesMap);
    setRoleDialogOpen(true);
  };

  const handlePageSelection = (pageName, checked) => {
    setSelectedPages(prev => ({
      ...prev,
      [pageName]: checked
    }));
    // Keep route map in sync
    const route = nameToRoute[pageName];
    if (route) {
      setSelectedRoutes(prev => ({ ...prev, [route]: checked }));
    }
  };

  const handleModuleSelection = (module, checked) => {
    const modulePages = pageModules[module];
    const newSelectedPages = { ...selectedPages };
    const newSelectedRoutes = { ...selectedRoutes };
    
    modulePages.forEach(page => {
      newSelectedPages[page.path] = checked;
      const route = nameToRoute[page.path];
      if (route) newSelectedRoutes[route] = checked;
    });
    
    setSelectedPages(newSelectedPages);
    setSelectedRoutes(newSelectedRoutes);
  };

  const getModuleSelectionStatus = (module) => {
    const modulePages = pageModules[module];
    const selectedCount = modulePages.filter(page => {
      const route = nameToRoute[page.path];
      return selectedPages[page.path] || (route && selectedRoutes[route]) || selectedRouteNames.has(page.path);
    }).length;
    
    if (selectedCount === 0) return 'none';
    if (selectedCount === modulePages.length) return 'all';
    return 'partial';
  };

  // Counts only the pages the PICKER offers.
  //
  // It used to prefer role.permissionsCount, then the raw permissions array —
  // both of which include grants the dialog does not show: pages for removed
  // modules, route aliases, and the always-available Messages and profile. So
  // a role's badge could read 31 while opening it showed 12 ticked, and there
  // was no way to reconcile the two.
  //
  // The extras are still held on the role and still preserved on save; they are
  // simply not part of a number that is meant to describe this dialog.
  const grantablePaths = React.useMemo(() => {
    const set = new Set();
    Object.values(pageModules).forEach((pages) => pages.forEach((p) => set.add(p.route)));
    return set;
  }, []);

  const getPermissionCount = (role) => {
    const routes = Array.isArray(role?.permissions) ? role.permissions : null;
    if (routes) return routes.filter((r) => grantablePaths.has(r)).length;
    // No permissions array on the row — fall back to the role's own defaults,
    // filtered the same way so the two are comparable.
    const defaults = getRoleMenuPages(role?.name || '');
    return Object.keys(defaults)
      .filter((k) => defaults[k])
      .filter((name) => {
        const route = nameToRoute[name] || NAME_TO_ROUTE[name];
        return route && grantablePaths.has(route);
      }).length;
  };

  const getRoleColor = (roleName) => {
    const colors = {
      [ROLES.SCHOOL_ADMIN]: 'primary',
      [ROLES.TEACHER]: 'success',
      [ROLES.CLASS_MASTER]: 'info',
      [ROLES.SUBJECT_HEAD]: 'warning',
      [ROLES.FINANCE_OFFICER]: 'error',
      [ROLES.LIBRARIAN]: 'default',
      [ROLES.TRANSPORT_OFFICER]: 'default',
      [ROLES.INVENTORY_MANAGER]: 'default',
      [ROLES.IT_SUPPORT]: 'default',
      [ROLES.ADMISSIONS_OFFICER]: 'default'
    };
    return colors[roleName] || 'default';
  };

  // THE PHASE-SCOPE BANNER IS GONE.
  //
  // It said "Managing roles for phase: NUR_PRI • School: 1", which is three
  // facts the reader either already has or cannot use: the scope bar above
  // every page names the phase and the year, the school is the one they signed
  // in to, and its numeric id is an internal key that means nothing to an
  // administrator. A permanent info alert on a page where nothing is wrong also
  // spends the reader's attention on a colour that should be reserved for when
  // something is.

  const roleCanonical = (user?.roleNormalized || user?.role || '').toString();
  const roleSlug = roleCanonical.toLowerCase().replace(/\s+/g, '').replace(/[^a-z]/g, '');
  const isSchoolAdmin = roleSlug === 'schooladmin';
  const isPrincipal = roleSlug === 'principal';
  const isHeadMaster = roleSlug === 'headmaster';
  const isPhaseHead = isPrincipal || isHeadMaster;
  const canCreate = isSchoolAdmin; // Only SchoolAdmin can create custom roles
  const canEditRole = (role) => {
    if (isSchoolAdmin) return true;
    return false; // Only SchoolAdmin can edit custom roles
  };
  const canDeleteRole = (role) => {
    if (!role) return false;
    if (isSchoolAdmin) return true; // Only SchoolAdmin can delete school-scoped roles
    return false;
  };

  const customVisibleRoles = React.useMemo(
    () =>
      (roles || [])
        .filter(r => r.schoolId != null && String(r.schoolId) === String(schoolId) && r.isSystemRole !== true),
    [roles, schoolId]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SecurityIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" component="h1">
            {t('rolePermissions.pageTitle')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('rolePermissions.pageSubtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">
                {systemTemplates.length + customVisibleRoles.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('rolePermissions.summary.totalRoles')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {systemTemplates.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('rolePermissions.summary.templateRoles')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                {customVisibleRoles.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('rolePermissions.summary.customRoles')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main">
                {Object.keys(pageModules).reduce((total, module) => total + pageModules[module].length, 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('rolePermissions.summary.availablePages')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {t('rolePermissions.manageRolesTitle')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateRole}
            disabled={!canCreate}
          >
            {t('rolePermissions.createRoleButton')}
          </Button>
        </Box>
      </Paper>

      {/* Template Roles Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('rolePermissions.templates.title')}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {t('rolePermissions.templates.subtitle')}
        </Typography>
        
        <Grid container spacing={2}>
          {systemTemplates.map((templateRole) => (
            <Grid item xs={12} sm={6} md={4} key={templateRole.id}>
              <Card variant="outlined" sx={{ 
                borderColor: templateRole.isSystemRole ? 'error.main' : 'primary.main',
                '&:hover': { 
                  boxShadow: 2,
                  borderColor: templateRole.isSystemRole ? 'error.dark' : 'primary.dark'
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="h3">
                      {templateRole.name}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {templateRole.description || t('rolePermissions.noDescriptionAvailable')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip
                      label={templateRole.isSystemRole ? t('rolePermissions.templates.systemTemplate') : t('rolePermissions.templates.customTemplate')}
                      color={templateRole.isSystemRole ? 'error' : 'primary'}
                      size="small"
                      variant="outlined"
                    />
                    <Box sx={{ ml: 'auto' }}>
                      <Chip
                        label={t('rolePermissions.templates.permissionCount', { count: getPermissionCount(templateRole) })}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleCreateFromTemplate(templateRole)}
                      disabled={!isSchoolAdmin}
                      fullWidth
                    >
                      {templateRole.isSystemRole ? t('rolePermissions.templates.createCustomRole') : t('rolePermissions.templates.useAsTemplate')}
                    </Button>
                    {/* No delete for global templates in grid */}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Roles Table - School Custom Roles & Templates */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 1 }}>
          {t('rolePermissions.table.customTemplatesTitle')}
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('rolePermissions.table.roleName')}</TableCell>
                <TableCell>{t('rolePermissions.table.description')}</TableCell>
                <TableCell>{t('rolePermissions.table.type')}</TableCell>
                <TableCell>{t('rolePermissions.table.permissions')}</TableCell>
                <TableCell>{t('rolePermissions.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customVisibleRoles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="textSecondary" sx={{ py: 4 }}>
                      {t('rolePermissions.table.noCustomTemplates')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                customVisibleRoles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <VpnKeyIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body1" fontWeight="medium">
                          {role.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {role.description || t('rolePermissions.table.noDescription')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={role.schoolId != null && role.isTemplate
                          ? t('rolePermissions.table.typeCustomTemplate')
                          : (role.isSystemRole
                            ? t('rolePermissions.table.typeSystemTemplate')
                            : (role.isTemplate
                              ? t('rolePermissions.table.typeTemplate')
                              : t('rolePermissions.table.typeCustom')))}
                        color={role.schoolId != null && role.isTemplate ? 'primary' : (role.isSystemRole ? 'error' : 'default')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Badge badgeContent={getPermissionCount(role)} color="primary">
                        <Chip
                          label={t('rolePermissions.table.pagesCount', { count: getPermissionCount(role) })}
                          color={getRoleColor(role.name)}
                          size="small"
                          variant="outlined"
                        />
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={t('rolePermissions.actions.viewPermissions')}>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleEditRole(role)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {/* Delete: allow for any school-scoped, non-system role; backend enforces creator/SchoolAdmin rules */}
                        {role.schoolId != null && role.isSystemRole !== true && (
                          <Tooltip title={role.isTemplate ? t('rolePermissions.actions.deleteTemplate') : t('rolePermissions.actions.deleteRole')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(role)}
                              disabled={!canDeleteRole(role)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {role.isTemplate && !role.isSystemRole && (
                          <Tooltip title={t('rolePermissions.actions.createFromTemplate')}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleCreateFromTemplate(role)}
                            >
                              <AddIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Role Dialog */}
      <Dialog 
        open={roleDialogOpen} 
        onClose={() => setRoleDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          {editingRole ? t('rolePermissions.dialog.editTitle') : t('rolePermissions.dialog.createTitle')}
          {editingRole && (
            <Typography variant="body2" color="textSecondary">
              {editingRole.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Basic Role Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('rolePermissions.dialog.roleNameLabel')}
                value={roleFormData.name}
                onChange={(e) => handleRoleNameChange(e.target.value)}
                placeholder={t('rolePermissions.dialog.roleNamePlaceholder')}
                helperText={t('rolePermissions.dialog.roleNameHelper')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('rolePermissions.dialog.descriptionLabel')}
                value={roleFormData.description}
                onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                placeholder={t('rolePermissions.dialog.descriptionPlaceholder')}
              />
            </Grid>
            
            {/* Page Permissions */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('rolePermissions.dialog.pagePermissions')}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {t('rolePermissions.dialog.pagePermissionsHelper')}
              </Typography>
              
              {/* Permission Philosophy Explanation */}
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('rolePermissions.dialog.permissionPhilosophyTitle')}</strong> {t('rolePermissions.dialog.permissionPhilosophy')}
                </Typography>
              </Alert>
              
              {/* Show default role permissions info */}
              {roleFormData.name.trim() && Object.values(ROLES).includes(roleFormData.name.trim()) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>{t('rolePermissions.dialog.defaultPermissionsLoaded')}</strong> {roleFormData.name.trim()}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('rolePermissions.dialog.defaultPermissionsLoadedHelper')}
                  </Typography>
                 </Alert>
              )}
              
              {Object.keys(pageModules).map((module) => {
                const moduleStatus = getModuleSelectionStatus(module);
                const modulePages = pageModules[module];
                const moduleKey = MODULE_KEY_BY_NAME[module];
                const moduleLabel = moduleKey
                  ? t(`rolePermissions.modules.${moduleKey}`, { defaultValue: module })
                  : module;
                
                return (
                  <Accordion 
                    key={module}
                    expanded={expandedPermissions[module]}
                    onChange={() => setExpandedPermissions(prev => ({
                      ...prev,
                      [module]: !prev[module]
                    }))}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Checkbox
                          checked={moduleStatus === 'all'}
                          indeterminate={moduleStatus === 'partial'}
                          onChange={(e) => handleModuleSelection(module, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                          {moduleLabel}
                        </Typography>
                        <Chip
                          label={`${modulePages.filter(page => {
                            const route = nameToRoute[page.path];
                            return selectedPages[page.path] || (route && selectedRoutes[route]) || selectedRouteNames.has(page.path);
                          }).length}/${modulePages.length}`}
                          size="small"
                          color={moduleStatus === 'all' ? 'success' : 'default'}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {modulePages.map((page) => {
                          const pageKey =
                            module === 'Parent Portal' && page.path === 'Messages'
                              ? 'parentMessages'
                              : PAGE_KEY_BY_NAME[page.path];
                          const pageName = pageKey
                            ? t(`rolePermissions.pages.${pageKey}.name`, { defaultValue: page.path })
                            : page.path;
                          const pageDescription = pageKey
                            ? t(`rolePermissions.pages.${pageKey}.description`, {
                                defaultValue: page.description,
                              })
                            : page.description;

                          return (
                            <ListItem key={page.path} sx={{ pl: 4 }}>
                              <ListItemIcon>
                                <Checkbox
                                  checked={
                                    Boolean(selectedPages[page.path]) ||
                                    Boolean(nameToRoute[page.path] && selectedRoutes[nameToRoute[page.path]]) ||
                                    selectedRouteNames.has(page.path)
                                  }
                                  onChange={(e) => handlePageSelection(page.path, e.target.checked)}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={pageName}
                                secondary={
                                  <Box>
                                    <Typography variant="body2" color="textSecondary">
                                      {pageDescription}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<CancelIcon />}
            onClick={() => setRoleDialogOpen(false)}
            disabled={saving}
          >
            {t('common.cancel')}
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSaveRole}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? (t('common.saving') || 'Saving...') : (t('common.saveChanges') || 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={deleteLoading ? undefined : handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t('rolePermissions.dialog.deleteTemplateTitle', {
            defaultValue: t('rolePermissions.actions.deleteTemplate') || 'Delete Template',
          })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {t('rolePermissions.dialog.deleteTemplateMessage', {
              name: rolePendingDelete?.name || '',
              defaultValue: 'Are you sure you want to delete the template "{{name}}"?',
            })}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('rolePermissions.dialog.deleteTemplateWarning', {
              defaultValue:
                'This action cannot be undone. If this template is assigned to users, deletion will be blocked.',
            })}
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} startIcon={<CancelIcon />} disabled={deleteLoading}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            startIcon={deleteLoading ? <CircularProgress size={18} /> : <DeleteIcon />}
            disabled={deleteLoading}
          >
            {deleteLoading
              ? (t('common.deleting') || 'Deleting...')
              : (t('rolePermissions.actions.deleteTemplate') || 'Delete Template')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolePermissionPage;
